import { Box, getVariant } from '@apia/theme';
import { ReactNode, useCallback, useRef } from 'react';

export const AutoscrollContainer = ({ children }: { children: ReactNode }) => {
  const observer = useRef<MutationObserver | null>(null);
  const unsuscribe = useRef<(() => void) | null>(null);
  const isFullScrolled = useRef(false);
  const hasInited = useRef(false);

  const ref = useCallback((el: HTMLElement) => {
    if (!el) return;

    if (!hasInited.current) {
      hasInited.current = true;
      el.scrollTo({
        top: el.getBoundingClientRect().height,
        behavior: 'auto',
      });
      isFullScrolled.current = true;
    }

    observer.current?.disconnect();
    unsuscribe.current?.();

    observer.current = new MutationObserver(([mutation]) => {
      if (mutation && mutation.target instanceof HTMLElement) {
        if (
          isFullScrolled.current &&
          el.scrollTop !== el.scrollHeight - el.offsetHeight
        ) {
          // eslint-disable-next-line no-param-reassign
          el.scrollTo({
            top: el.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
    });
    observer.current.observe(el, { childList: true, subtree: true });

    const handleScroll = (ev: Event) => {
      if (ev.target === el && el instanceof HTMLElement) {
        setTimeout(() => {
          isFullScrolled.current =
            Math.abs(el.scrollTop - (el.scrollHeight - el.offsetHeight)) < 150;
        }, 100);
      }
    };
    el.addEventListener('scroll', handleScroll);
    unsuscribe.current = () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box
      {...getVariant('layout.common.components.autoscrollContainer')}
      className="autoscrollContainer"
      ref={ref}
    >
      {children}
    </Box>
  );
};
